// Recovers from stale lazy-route chunks after a deploy. The open tab still
// references the previous build's hashed chunk files, which the new deploy
// removed — so the dynamic import 404s and navigation fails silently. A full
// reload picks up the new index.html and its chunk map.
//
// The reload is guarded by a timestamp so a chunk that is genuinely broken
// (not just stale) can't trap the tab in a reload loop.

import type { Router } from 'vue-router'
import constants from '@core/config/constants'
import storageService from '@core/persistence/localStorage/localStorageService'
import { logger } from '@core/logging/logger'

const RELOAD_GUARD_MS = 10_000
const CHUNK_LOAD_ERROR_PATTERN =
  /Failed to fetch dynamically imported module|Importing a module script failed|error loading dynamically imported module/i

const isChunkLoadError = (error: unknown): boolean =>
  error instanceof Error && CHUNK_LOAD_ERROR_PATTERN.test(error.message)

// Returns false when a reload already happened inside the guard window.
const reloadOnce = (targetPath?: string): boolean => {
  const key = constants.LOCAL_STORAGE_KEYS.CHUNK_RELOAD_AT
  const lastReloadAt = Number(storageService.getItem(key) ?? 0)
  if (Date.now() - lastReloadAt < RELOAD_GUARD_MS) return false
  storageService.setItem(key, String(Date.now()))
  logger.warn('Stale build chunk detected — reloading', { targetPath })
  if (targetPath) window.location.assign(targetPath)
  else window.location.reload()
  return true
}

export const initChunkReload = (router: Router): void => {
  // Vite fires this when a preload of a lazy chunk fails.
  window.addEventListener('vite:preloadError', (event) => {
    if (reloadOnce()) event.preventDefault()
  })

  // Lazy route components that fail to import surface here.
  router.onError((error, to) => {
    if (isChunkLoadError(error)) reloadOnce(to.fullPath)
  })
}
